// Phase P.8 — Global manager search. Desktop = persistent bar in top
// header. Mobile = icon that opens a full-screen modal. Both share the
// same GroupedResults component so behaviour stays identical.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, Users, Wrench, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatBookingStamp } from "@/lib/dates";

// ---------------------------------------------------------------- helpers
function useDebounced(value, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function formatMoney(n) {
  const v = Number(n) || 0;
  return `EUR ${v.toFixed(2)}`;
}

// ---------------------------------------------------------------- row (extracted so eslint's no-unstable-nested-components is happy)
function SearchResultRow({ kind, row, focused, onSelect }) {
  if (kind === "job") {
    return (
      <button
        type="button"
        onClick={() => onSelect(`/jobs/${row.id}`)}
        className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 ${
          focused ? "bg-accent/15" : "hover:bg-secondary"
        }`}
        data-testid={`global-search-job-${row.id}`}
      >
        <Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold truncate">{row.customer_name || "—"} · <span className="mono text-[10px] uppercase text-muted-foreground">{row.status}</span></div>
          <div className="text-[11px] text-muted-foreground truncate">
            {row.job_template || "Job"} · {row.address || "—"} · {(row.scheduled_start || "").slice(0, 10)}
          </div>
          {row.created_at && (
            <div
              className="text-[10px] text-muted-foreground mono tabular-nums truncate"
              data-testid={`global-search-job-${row.id}-created-at`}
            >
              Booked {formatBookingStamp(row.created_at)}
            </div>
          )}
        </div>
      </button>
    );
  }
  if (kind === "customer") {
    return (
      <button
        type="button"
        onClick={() => onSelect(`/customers/${row.id}`)}
        className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 ${
          focused ? "bg-accent/15" : "hover:bg-secondary"
        }`}
        data-testid={`global-search-customer-${row.id}`}
      >
        <Users className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold truncate">{row.company || row.contact_name || "—"}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {row.contact_name}{row.contact_name && (row.primary_phone || row.primary_email) ? " · " : ""}
            {row.primary_phone}{row.primary_phone && row.primary_email ? " · " : ""}{row.primary_email}
          </div>
        </div>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(`/invoices/${row.id}`)}
      className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 ${
        focused ? "bg-accent/15" : "hover:bg-secondary"
      }`}
      data-testid={`global-search-invoice-${row.id}`}
    >
      <Receipt className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold truncate">{row.invoice_number} · {row.customer_name || "—"}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {formatMoney(row.amount_total)} · <span className="mono uppercase">{row.status}</span> · {(row.issued_at || "").slice(0, 10)}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------- grouped results
function GroupedResults({ results, loading, query, onSelect, focusIdx }) {
  const flat = useMemo(() => {
    if (!results) return [];
    return [
      ...(results.jobs || []).map((r, i) => ({ kind: "job", row: r, key: `j-${r.id}`, seq: i })),
      ...(results.customers || []).map((r, i) => ({ kind: "customer", row: r, key: `c-${r.id}`, seq: i })),
      ...(results.invoices || []).map((r, i) => ({ kind: "invoice", row: r, key: `i-${r.id}`, seq: i })),
    ];
  }, [results]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 px-4" data-testid="global-search-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
      </div>
    );
  }
  if (!query || query.length < 2) {
    return (
      <div className="text-xs text-muted-foreground py-6 px-4" data-testid="global-search-hint">
        Type at least 2 characters — company name, phone, email, address, invoice number…
      </div>
    );
  }
  if (flat.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-6 px-4" data-testid="global-search-empty">
        No results for <span className="mono font-bold text-foreground">{query}</span>.
      </div>
    );
  }
  const jobs = results.jobs || [];
  const customers = results.customers || [];
  const invoices = results.invoices || [];

  let idx = 0;
  const renderGroup = (title, rows, kind, viewAllTo) => {
    if (!rows.length) return null;
    return (
      <div className="mt-1" data-testid={`global-search-group-${kind}`}>
        <div className="flex items-center justify-between px-3 pt-2 pb-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{title}</div>
          {rows.length >= 10 && viewAllTo && (
            <button
              type="button"
              onClick={() => onSelect(viewAllTo)}
              className="text-[10px] uppercase tracking-widest font-bold text-accent hover:underline"
              data-testid={`global-search-view-all-${kind}`}
            >
              View all
            </button>
          )}
        </div>
        <div className="space-y-0.5">
          {rows.map((row) => {
            const currentIdx = idx;
            idx += 1;
            return (
              <SearchResultRow key={`${kind}-${row.id}`} kind={kind} row={row} focused={currentIdx === focusIdx} onSelect={onSelect} />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="pb-2" data-testid="global-search-results">
      {renderGroup("Jobs", jobs, "job", `/jobs?q=${encodeURIComponent(query)}`)}
      {renderGroup("Customers", customers, "customer", `/customers?q=${encodeURIComponent(query)}`)}
      {renderGroup("Invoices", invoices, "invoice", `/accounts?q=${encodeURIComponent(query)}`)}
    </div>
  );
}

// ---------------------------------------------------------------- desktop bar
export function GlobalSearchDesktop() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const containerRef = useRef(null);
  const debounced = useDebounced(query, 250);

  useEffect(() => {
    const q = (debounced || "").trim();
    if (q.length < 2) { setResults(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/search", { params: { q, limit: 10 } })
      .then(({ data }) => { if (!cancelled) { setResults(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setResults(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [debounced]);

  // Click-outside close.
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const flatCount = useMemo(() => {
    if (!results) return 0;
    return (results.jobs?.length || 0) + (results.customers?.length || 0) + (results.invoices?.length || 0);
  }, [results]);

  const flatList = useMemo(() => {
    if (!results) return [];
    return [
      ...(results.jobs || []).map((r) => `/jobs/${r.id}`),
      ...(results.customers || []).map((r) => `/customers/${r.id}`),
      ...(results.invoices || []).map((r) => `/invoices/${r.id}`),
    ];
  }, [results]);

  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min((flatCount - 1), i + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); return; }
    if (e.key === "Enter") {
      if (focusIdx >= 0 && focusIdx < flatList.length) {
        e.preventDefault();
        setOpen(false);
        navigate(flatList[focusIdx]);
      }
    }
  };

  const onSelect = useCallback((to) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setFocusIdx(-1);
    navigate(to);
  }, [navigate]);

  return (
    <div ref={containerRef} className="relative w-64 lg:w-80" data-testid="global-search-desktop">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setFocusIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search jobs, customers, invoices…"
          className="pl-8 h-9 text-sm"
          data-testid="global-search-input-desktop"
        />
      </div>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded-md shadow-lg z-40 max-h-[420px] overflow-auto"
             data-testid="global-search-dropdown">
          <GroupedResults results={results} loading={loading} query={query} onSelect={onSelect} focusIdx={focusIdx} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- mobile modal
export function GlobalSearchMobile() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const inputRef = useRef(null);
  const debounced = useDebounced(query, 250);

  useEffect(() => {
    const q = (debounced || "").trim();
    if (q.length < 2) { setResults(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/search", { params: { q, limit: 10 } })
      .then(({ data }) => { if (!cancelled) { setResults(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setResults(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [debounced]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const onSelect = useCallback((to) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setFocusIdx(-1);
    navigate(to);
  }, [navigate]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="sm:hidden h-9 w-9 p-0"
        onClick={() => setOpen(true)}
        aria-label="Open search"
        data-testid="global-search-mobile-trigger"
      >
        <Search className="h-4 w-4" />
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col sm:hidden" data-testid="global-search-mobile-modal">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setFocusIdx(-1); }}
                placeholder="Search jobs, customers, invoices…"
                className="pl-8 h-10 text-sm"
                data-testid="global-search-input-mobile"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} aria-label="Close search" data-testid="global-search-mobile-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-auto">
            <GroupedResults results={results} loading={loading} query={query} onSelect={onSelect} focusIdx={focusIdx} />
          </div>
        </div>
      )}
    </>
  );
}
